import React, {
  ReactNode,
  Ref,
  UIEventHandler,
  useCallback,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from "react"
import { useInView } from "react-intersection-observer"
import { match } from "ts-pattern"
import { useDebounceValue } from "usehooks-ts"

import { cn } from "@/lib/utils"
import useResizeObserver, {
  UseResizeObserverCallback,
} from "@react-hook/resize-observer"
import {
  IconArrowsSort,
  IconDots,
  IconDotsVertical,
  IconInfoCircle,
  IconInfoTriangle,
  IconLock,
  IconSortAscending,
  IconSortDescending,
} from "@tabler/icons-react"
import {
  Cell,
  ColumnDef,
  ColumnFiltersState,
  ColumnPinningState,
  PaginationState,
  TableOptions as ReactTableOptions,
  Row,
  RowSelectionState,
  SortingState,
  Table,
  TableMeta,
  VisibilityState,
  flexRender,
} from "@tanstack/react-table"
import { notUndefined, useVirtualizer } from "@tanstack/react-virtual"

import { Button } from "../Button"
import { Checkbox } from "../Checkbox"
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from "../DropdownMenu"
import { EmptyState } from "../EmptyState"
import * as RadioGroup from "../RadioGroup"
import { Spinner } from "../Spinner"
import { Tooltip } from "../Tooltip"
import { BulkActionsFloater } from "./BulkActionsFloater"
import { ClearFiltersButton } from "./actions/ClearFiltersButton"
import { ColumnsVisibilityButton } from "./actions/ColumnsVisibilityButton"
import { ExportButton } from "./actions/ExportButton"
import { ToggleFullscreenButton } from "./actions/ToggleFullscreenButton"
import { ActiveFiltersList } from "./filters/ActiveFiltersList"
import { FilterButton } from "./filters/FilterButton"
import { GlobalFilter } from "./filters/GlobalFilter"
import { Footer } from "./footer/Footer"
import { ColumnDropdown } from "./header/ColumnDropdown"
import { LE_TableInstance, LE_TableOptions } from "./types"
import { useLumenTable } from "./useTable"

const KEBAB_MENU_BUTTON_ID_SUFFIX = "kebab-menu-button"
const FLOATING_KEBAB_MENU_BUTTON_ID_SUFFIX = "floating-kebab-menu-button"

export interface DataTableProps<TData extends Record<string, any> = object> {
  id?: string
  data: TData[]
  columns: ColumnDef<TData, any>[]

  isLoading?: boolean
  hasError?: boolean
  fetchMore?: () => void
  total?: number
  getIsRowLoading?: (row: Row<TData>) => boolean
  getRowId?: LE_TableOptions<TData>["getRowId"]
  activeRowId?: string
  emptyState?: ReactNode
  errorState?: ReactNode
  onRowClick?: (row: Row<TData>) => void
  className?: string
  classNameHeader?: string
  classNameFooter?: string
  classNameCell?: string | ((cell: Cell<TData, unknown>) => string)
  tableContainerClassName?: string

  sorting?: SortingState
  defaultSorting?: SortingState
  onSortingChange?: LE_TableOptions<TData>["onSortingChange"]
  manualSorting?: boolean

  columnVisibility?: VisibilityState
  defaultColumnVisibility?: VisibilityState
  /** Adding this handler will make the columnVisibility state controlled */
  onColumnVisibilityChange?: LE_TableOptions<TData>["onColumnVisibilityChange"]

  columnFilters?: ColumnFiltersState
  defaultColumnFilters?: ColumnFiltersState
  onColumnFiltersChange?: LE_TableOptions<TData>["onColumnFiltersChange"]
  manualFiltering?: boolean

  pagination?: PaginationState
  defaultPagination?: PaginationState
  onPaginationChange?: LE_TableOptions<TData>["onPaginationChange"]
  manualPagination?: boolean
  enablePagination?: boolean

  rowSelection?: RowSelectionState
  defaultRowSelection?: RowSelectionState
  onRowSelectionChange?: LE_TableOptions<TData>["onRowSelectionChange"]
  enableRowSelection?: boolean
  enableMultiRowSelection?: boolean
  renderBulkActions?: LE_TableOptions<TData>["renderBulkActions"]
  rowSelectedText?: string

  columnPinning?: ColumnPinningState
  defaultColumnPinning?: ColumnPinningState
  /** Adding this handler will make the columnPinning state controlled */
  onColumnPinningChange?: LE_TableOptions<TData>["onColumnPinningChange"]

  globalFilter?: string
  /** Initial global filter value */
  defaultGlobalFilter?: string
  /** Adding this handler will make the globalFilter state controlled */
  onGlobalFilterChange?: ReactTableOptions<TData>["onGlobalFilterChange"]

  renderKebabMenuContent?: (item: TData) => ReactNode
  renderCustomActions?: (args: {
    table: LE_TableInstance<TData>
    data: TData[]
  }) => ReactNode
  limit?: number
  tableLayout?: "fixed" | "auto"

  enableColumnVisibility?: boolean
  enableFullscreenToggle?: boolean
  showFilters?: boolean
  showGlobalSearch?: boolean | "always"
  showFooter?: boolean
  showColumnKebabMenu?: boolean
  debounceGlobalSearch?: boolean | number
  virtualizeRows?: boolean

  /** This is a function that returns the rows to be exported, to be used when the table is server-side paginated */
  getExportRows?: () => Promise<TData[]>
  enableExportButton?: boolean
  exportFilename?: string

  onUpdateData?: TableMeta<TData>["updateData"]

  tableRef?: Ref<LE_TableInstance<TData>>

  customRowRender?: (row: Row<TData>) => ReactNode
}

const MORE_BUTTON_HIDE_THRESHOLD = 50

export const SELECTABLE_COLUMN_ID = "selectable-column"
export const KEBAB_MENU_COLUMN_ID = "kebab-menu-column"

export function DataTable<TData extends Record<string, any> = object>({
  id,
  data,
  columns: externalColumns,
  isLoading = false,
  hasError = false,
  fetchMore,
  total,
  getIsRowLoading,
  getRowId,
  activeRowId,
  emptyState = (
    <EmptyState
      icon={IconInfoTriangle}
      title="No data available"
      className="rounded-none"
    />
  ),
  errorState,
  onRowClick,

  className,
  classNameHeader,
  classNameFooter,
  classNameCell,
  tableContainerClassName,

  sorting,
  defaultSorting = [],
  onSortingChange,
  manualSorting,

  columnVisibility,
  defaultColumnVisibility,
  onColumnVisibilityChange,

  columnFilters,
  defaultColumnFilters = [],
  onColumnFiltersChange,
  manualFiltering,

  pagination,
  defaultPagination,
  onPaginationChange,
  manualPagination,
  enablePagination = false,

  rowSelection,
  defaultRowSelection = {},
  onRowSelectionChange,
  enableRowSelection = false,
  enableMultiRowSelection = false,
  renderBulkActions,
  rowSelectedText,

  columnPinning,
  defaultColumnPinning = {
    left: [SELECTABLE_COLUMN_ID],
    right: [],
  },
  onColumnPinningChange,

  globalFilter,
  defaultGlobalFilter,
  onGlobalFilterChange,

  renderKebabMenuContent,
  renderCustomActions,
  limit,
  tableLayout = "auto",

  enableColumnVisibility = true,
  enableFullscreenToggle = false,
  showFilters = false,
  showGlobalSearch = false,
  showFooter = false,
  showColumnKebabMenu = false,
  debounceGlobalSearch = false,
  virtualizeRows = false,

  enableExportButton = false,
  getExportRows,
  exportFilename,

  onUpdateData,

  tableRef: instanceRef,

  customRowRender,
}: DataTableProps<TData>) {
  const [containerWidth, setContainerWidth] = useDebounceValue(0, 100)
  const [containerScrollLeft, setContainerScrollLeft] = useDebounceValue(0, 100)
  const [tableWidth, setTableWidth] = useDebounceValue(0, 100)
  const [activeMoreMenuRowId, setActiveMoreMenuRowId] = useState<string>()

  const { ref: inViewRef } = useInView({
    skip: !fetchMore,
    onChange(inView) {
      if (inView && !isLoading) {
        fetchMore?.()
      }
    },
  })
  const tableRef = useRef<HTMLTableElement>(null)
  const tableContainerRef = useRef<HTMLDivElement>(null)

  const tableContainerResizeCallback = useCallback<UseResizeObserverCallback>(
    (entry) => {
      setContainerWidth(entry.target.clientWidth)
    },
    [setContainerWidth],
  )
  useResizeObserver(tableContainerRef, tableContainerResizeCallback)

  const tableResizeCallback = useCallback<UseResizeObserverCallback>(
    (entry) => {
      setTableWidth(entry.target.clientWidth)
    },
    [setTableWidth],
  )
  useResizeObserver(tableRef, tableResizeCallback)

  const handleContainerScroll: UIEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      const scrollLeft = event.currentTarget.scrollLeft
      setContainerScrollLeft(scrollLeft)
    },
    [setContainerScrollLeft],
  )

  const showMoreButton = useMemo(() => {
    return (
      containerWidth + containerScrollLeft + MORE_BUTTON_HIDE_THRESHOLD < tableWidth
    )
  }, [containerScrollLeft, containerWidth, tableWidth])

  const columns = useMemo(() => {
    let cols = [...externalColumns]

    const selectCol: ColumnDef<TData> = {
      id: SELECTABLE_COLUMN_ID,
      size: 48,
      maxSize: 48,
      minSize: 48,
      meta: {
        fixedSize: true,
      },
      header: ({ table }) => {
        if (!enableMultiRowSelection) return null
        const checked =
          table.getIsAllPageRowsSelected() ||
          (table.getIsSomePageRowsSelected() ? "indeterminate" : false)
        return (
          <Checkbox
            checked={checked}
            onCheckedChange={(checked) => table.toggleAllPageRowsSelected(!!checked)}
            className="pt-0.5"
          />
        )
      },
      cell: ({ row }) => {
        if (!enableMultiRowSelection)
          return (
            <RadioGroup.Root
              className="p-1"
              value={row.getIsSelected() ? row.id : ""}
              onValueChange={() => row.toggleSelected()}
              onClick={(event) => event.stopPropagation()}
            >
              <RadioGroup.Item value={row.id} checked={row.getIsSelected()} />
            </RadioGroup.Root>
          )

        return (
          <Checkbox
            checked={row.getIsSelected()}
            onCheckedChange={() => row.toggleSelected()}
          />
        )
      },
    }

    if (renderKebabMenuContent) {
      cols.push({
        id: KEBAB_MENU_COLUMN_ID,
        size: 42,
        enableHiding: false,
        meta: {
          fixedSize: true,
        },
      })
    }

    if (enableRowSelection) cols = [selectCol, ...cols]
    return cols
  }, [
    externalColumns,
    renderKebabMenuContent,
    enableRowSelection,
    enableMultiRowSelection,
  ])

  const tableOptions: LE_TableOptions<TData> = {
    data,
    columns,
    onSortingChange,
    onColumnVisibilityChange,
    onColumnFiltersChange,
    onColumnPinningChange,
    onPaginationChange,
    onRowSelectionChange,
    manualFiltering,
    manualPagination,
    manualSorting,
    enablePagination,
    enableRowSelection,
    enableMultiRowSelection,
    state: {
      sorting,
      columnVisibility,
      columnFilters,
      columnPinning,
      globalFilter,
      pagination,
      rowSelection,
      total: total ?? data.length,
    },
    initialState: {
      columnVisibility: defaultColumnVisibility,
      columnFilters: defaultColumnFilters,
      sorting: defaultSorting,
      globalFilter: defaultGlobalFilter || "",
      pagination: defaultPagination,
      rowSelection: defaultRowSelection,
      columnPinning: defaultColumnPinning,
    },
    onGlobalFilterChange,
    tableId: id,
    debounceGlobalSearch,
    getRowId,
    renderBulkActions,
    isLoading,
    getExportRows,
    meta: {
      updateData: onUpdateData,
    },
  }

  const table = useLumenTable<TData>(tableOptions)

  useImperativeHandle(instanceRef, () => table, [table])

  const state = table.getState()

  const { rows: allRows } = table.getRowModel()

  const rows = useMemo(() => {
    if (limit) {
      return allRows.slice(0, limit)
    }
    return allRows
  }, [allRows, limit])

  const renderSpinner = (table: Table<TData>) => (
    <td colSpan={table.getAllFlatColumns().length} className="align-middle">
      <Spinner
        className="sticky left-0 h-[45px]"
        key={`placeholder-1`}
        style={{
          width: containerWidth - 2,
        }}
      />
    </td>
  )

  const virtualizer = useVirtualizer({
    count: virtualizeRows ? rows.length : 0,
    getScrollElement: () => tableContainerRef.current,
    estimateSize: () => 44,
    overscan: 20,
  })

  const virtualRows = virtualizeRows ? virtualizer.getVirtualItems() : []
  const [before, after] =
    virtualRows.length > 0
      ? [
          notUndefined(virtualRows[0]).start - virtualizer.options.scrollMargin,
          virtualizer.getTotalSize() -
            notUndefined(virtualRows[virtualRows.length - 1]).end,
        ]
      : [0, 0]

  const showActionsHeader =
    enableColumnVisibility || enableFullscreenToggle || showFilters || showGlobalSearch

  const showLoadingRow = showFooter ? data.length === 0 && isLoading : isLoading

  return (
    <div
      id={table.tableId}
      className={cn(
        "isolate flex h-fit max-h-full w-full flex-col overflow-auto rounded",
        className,
        {
          "fixed top-0 left-0 z-modal h-screen max-h-screen w-screen max-w-screen bg-white":
            state.isFullscreen,
        },
      )}
    >
      {showActionsHeader && (
        <div
          className={cn(
            "flex flex-col gap-2 border-b border-colors-border-secondary bg-colors-bg-primary-screen p-3",
            classNameHeader,
          )}
        >
          <div className="flex justify-between gap-4">
            <div className="flex h-fit items-center gap-2">
              {showFilters ? (
                <>
                  <FilterButton table={table} columnFilters={state.columnFilters} />
                  <ClearFiltersButton table={table} />
                </>
              ) : null}
            </div>
            <div className="flex grow flex-wrap items-center justify-end gap-2">
              {showGlobalSearch ? (
                <>
                  <GlobalFilter.Input
                    table={table}
                    visible={showGlobalSearch === "always" ? true : undefined}
                    className="max-w-64"
                  />
                  <GlobalFilter.ToggleButton table={table} />
                </>
              ) : null}
              {renderCustomActions?.({ table, data }) ?? null}
              {enableColumnVisibility ? (
                <ColumnsVisibilityButton table={table} />
              ) : null}
              {enableFullscreenToggle ? <ToggleFullscreenButton table={table} /> : null}
              {enableExportButton ? (
                <ExportButton
                  table={table}
                  data={data}
                  exportFilename={exportFilename}
                />
              ) : null}
            </div>
          </div>
          {showFilters && (
            <ActiveFiltersList table={table} columnFilters={state.columnFilters} />
          )}
        </div>
      )}
      <BulkActionsFloater table={table} rowSelectedText={rowSelectedText} />

      <div
        className={cn(
          "flex h-fit min-h-0 w-full grow flex-col overflow-auto",
          tableContainerClassName,
        )}
        onScroll={handleContainerScroll}
        ref={tableContainerRef}
      >
        <div className="relative flex grow flex-col">
          {customRowRender ? (
            (virtualizeRows ? virtualRows : rows).map((rowOrVirtualRow) => {
              const row = virtualizeRows
                ? rows[rowOrVirtualRow.index]
                : (rowOrVirtualRow as Row<TData>)

              if (!row || getIsRowLoading?.(row)) {
                return <tr key={`loading-row-${row.index}`}>{renderSpinner(table)}</tr>
              }
              return customRowRender?.(row)
            })
          ) : (
            <table
              className={cn("border-separate border-spacing-0", {
                "table-auto": tableLayout === "auto",
                "table-fixed": tableLayout === "fixed",
              })}
              style={{
                width: Math.max(containerWidth, table.getCenterTotalSize()),
              }}
              ref={tableRef}
            >
              <thead
                className={cn(
                  "z-20 p-3",
                  "border border-solid border-colors-border-secondary",
                  classNameHeader,
                )}
              >
                {table.getHeaderGroups().map((headerGroup, index, headerGroups) => {
                  const isLastHeaderRow = index === headerGroups.length - 1

                  return (
                    <tr
                      key={headerGroup.id}
                      className="sticky z-20 items-center"
                      style={{
                        top: `${index * 2.5}rem`,
                        height: "2.25rem",
                      }}
                    >
                      {headerGroup.headers.map((header) => {
                        const HeaderIcon = header.column.columnDef.meta?.icon
                        const hasChildren = header.column.getFlatColumns().length > 1
                        const colParent = header.column.parent
                        const lastParentChild =
                          colParent?.getLeafColumns().at(-1)?.id === header.column.id

                        const isGroupPinned = header.subHeaders.some(
                          (subHeader) => subHeader.column.getIsPinned() === "left",
                        )
                        const isPinned = hasChildren
                          ? isGroupPinned
                          : header.column.getIsPinned() === "left"

                        let left = header.column.getStart("left")
                        if (hasChildren) {
                          left = header.getLeafHeaders().at(0)?.getStart("left") ?? 0
                        }

                        return (
                          <th
                            className={cn(
                              "sticky top-0 z-20 bg-colors-bg-primary-screen px-4 py-2 text-start align-top text-sm whitespace-nowrap text-colors-text-secondary",
                              "border-solid border-colors-border-secondary font-normal last-of-type:border-r-0",

                              isLastHeaderRow ? "border-b-2" : "",
                              isPinned && "z-30",
                              hasChildren && "border-r p-1 font-medium",
                              lastParentChild && "border-r",
                              classNameHeader,
                            )}
                            key={header.id}
                            style={{
                              width: header.column.columnDef.size
                                ? header.getSize()
                                : undefined,
                              ...(header.column.columnDef.meta?.fixedSize
                                ? {
                                    minWidth: header.getSize(),
                                    maxWidth: header.getSize(),
                                  }
                                : {}),
                              ...(isPinned
                                ? {
                                    left: `${left}px`,
                                    width: header.getSize(),
                                    minWidth: header.getSize(),
                                    maxWidth: header.getSize(),
                                  }
                                : {}),
                            }}
                            colSpan={header.colSpan}
                          >
                            {header.isPlaceholder ? null : (
                              <div
                                className={cn(
                                  "flex h-full cursor-default items-center justify-between gap-3 overflow-hidden rounded",
                                  hasChildren && "bg-sand-300 px-3 py-1.5",
                                )}
                              >
                                <span className="flex items-center gap-3">
                                  {HeaderIcon ? <HeaderIcon size={16} /> : null}
                                  <span
                                    className={cn("flex w-fit items-center gap-1", {
                                      "sr-only":
                                        header.column.columnDef.meta?.hideHeader,
                                      "cursor-pointer": header.column.getCanSort(),
                                    })}
                                    onClick={header.column.getToggleSortingHandler()}
                                  >
                                    {flexRender(
                                      header.column.columnDef.header,
                                      header.getContext(),
                                    )}
                                    {header.column.getCanSort() ? (
                                      <Tooltip
                                        content="Toggle sorting"
                                        dark
                                        disableHoverableContent
                                      >
                                        <button
                                          aria-label="toggle sorting"
                                          type="button"
                                        >
                                          {match(header.column.getIsSorted())
                                            .with(false, () => (
                                              <IconArrowsSort size={16} />
                                            ))
                                            .with("asc", () => (
                                              <IconSortAscending
                                                size={16}
                                                className="text-colors-icon-brand"
                                              />
                                            ))
                                            .with("desc", () => (
                                              <IconSortDescending
                                                size={16}
                                                className="text-colors-icon-brand"
                                              />
                                            ))
                                            .exhaustive()}
                                        </button>
                                      </Tooltip>
                                    ) : null}
                                  </span>
                                </span>
                                {showColumnKebabMenu ||
                                header.column.columnDef.meta?.tooltip ||
                                header.column.columnDef.meta?.adminOnly ? (
                                  <span className="flex items-center gap-2">
                                    {header.column.columnDef.meta?.tooltip ? (
                                      <Tooltip
                                        content={header.column.columnDef.meta.tooltip}
                                        className="max-w-sm"
                                        dark
                                      >
                                        <IconInfoCircle />
                                      </Tooltip>
                                    ) : null}
                                    {header.column.columnDef.meta?.adminOnly && (
                                      <Tooltip
                                        content="This column is admin-only. Non-admin users will be unable to see it."
                                        className="max-w-sm"
                                        dark
                                        disableHoverableContent
                                      >
                                        <IconLock />
                                      </Tooltip>
                                    )}
                                    {showColumnKebabMenu ? (
                                      <ColumnDropdown
                                        col={header.column}
                                        table={table}
                                      />
                                    ) : null}
                                  </span>
                                ) : null}
                              </div>
                            )}
                          </th>
                        )
                      })}
                    </tr>
                  )
                })}
              </thead>
              {hasError && !isLoading ? (
                <tbody></tbody>
              ) : (
                <tbody>
                  {before > 0 && (
                    <tr>
                      <td
                        colSpan={table.getAllFlatColumns().length}
                        style={{ height: before, printColorAdjust: "exact" }}
                      />
                    </tr>
                  )}
                  {(virtualizeRows ? virtualRows : rows).map((rowOrVirtualRow) => {
                    const row = virtualizeRows
                      ? rows[rowOrVirtualRow.index]
                      : (rowOrVirtualRow as Row<TData>)

                    if (!row || getIsRowLoading?.(row)) {
                      return (
                        <tr key={`loading-row-${row.index}`}>{renderSpinner(table)}</tr>
                      )
                    }

                    return (
                      <Memo_TableBodyRow<TData>
                        table={table}
                        key={`${row.id}-${enableRowSelection}-${enableMultiRowSelection}`}
                        row={row}
                        onRowClick={onRowClick}
                        renderKebabMenuContent={renderKebabMenuContent}
                        showMoreButton={showMoreButton}
                        setActiveMoreMenuRowId={setActiveMoreMenuRowId}
                        active={activeRowId === row.id}
                        moreMenuActive={row.id === activeMoreMenuRowId}
                        classNameCell={classNameCell}
                      />
                    )
                  })}
                  {total !== undefined && total - data.length > 0 && fetchMore && (
                    <tr
                      ref={inViewRef}
                      key={`placeholder-container-${total - data.length}-${isLoading}`}
                    >
                      <td
                        colSpan={table.getAllFlatColumns().length}
                        className="align-middle"
                      >
                        <Spinner
                          className="sticky left-0 h-[45px]"
                          key={`placeholder`}
                          style={{ width: containerWidth - 2 }}
                        />
                      </td>
                    </tr>
                  )}
                  {showLoadingRow ? <tr>{renderSpinner(table)}</tr> : null}
                  {after > 0 && (
                    <tr>
                      <td
                        colSpan={table.getAllFlatColumns().length}
                        style={{ height: after, printColorAdjust: "exact" }}
                      />
                    </tr>
                  )}
                </tbody>
              )}
            </table>
          )}

          {(hasError || rows.length === 0) && containerWidth && !isLoading ? (
            <div
              className="relative flex h-full grow flex-col"
              style={{
                width: Math.max(containerWidth, table.getCenterTotalSize()),
              }}
            >
              <div
                className="sticky left-0 grid grow grid-cols-1 grid-rows-1"
                style={{ width: containerWidth }}
              >
                {hasError ? errorState : rows.length === 0 ? emptyState : null}
              </div>
            </div>
          ) : null}
        </div>
      </div>
      {showFooter && <Footer table={table} className={classNameFooter} />}
    </div>
  )
}

function TableBodyRow<TData extends Record<string, any> = object>({
  table,
  row,
  onRowClick,
  renderKebabMenuContent,
  showMoreButton,
  setActiveMoreMenuRowId,
  active,
  moreMenuActive,
  classNameCell,
  style,
}: {
  table: LE_TableInstance<TData>
  row: Row<TData>
  onRowClick?: DataTableProps<TData>["onRowClick"]
  renderKebabMenuContent?: DataTableProps<TData>["renderKebabMenuContent"]
  showMoreButton: boolean
  setActiveMoreMenuRowId: React.Dispatch<React.SetStateAction<string | undefined>>
  active?: boolean
  moreMenuActive?: boolean
  classNameCell?: string | ((cell: Cell<TData, unknown>) => string)
  style?: React.CSSProperties
}) {
  const kebabMenuContent = renderKebabMenuContent?.(row.original)

  return (
    <tr
      key={row.id}
      className={cn(
        "group h-11 bg-white transition-colors hover:bg-colors-bg-primary-screen focus-visible:bg-colors-bg-primary-screen aria-selected:bg-brand-primary-100 [&:last-of-type>td]:border-b-0",
        onRowClick ? "cursor-pointer" : null,
      )}
      aria-selected={active || moreMenuActive || row.getIsSelected()}
      onClick={() => onRowClick?.(row)}
      tabIndex={onRowClick ? 0 : undefined}
      style={style}
    >
      {row.getVisibleCells().map((cell) => {
        if (cell.column.id === KEBAB_MENU_COLUMN_ID) {
          return (
            <td
              className={cn(
                "sticky -right-12 h-full w-0",
                "align-bottom text-black",
                "border-r border-b border-solid border-colors-border-secondary last-of-type:border-r-0",
                typeof classNameCell === "function"
                  ? classNameCell(cell)
                  : classNameCell,
              )}
              key={cell.id}
              style={{
                width: 42,
                minWidth: 42,
                maxWidth: 42,
              }}
            >
              {kebabMenuContent ? (
                <>
                  <DropdownMenu
                    onOpenChange={(open) => {
                      setActiveMoreMenuRowId(open ? row.id : undefined)
                    }}
                    modal={false}
                  >
                    <DropdownMenuTrigger asChild>
                      <button
                        id={table.getElementId(FLOATING_KEBAB_MENU_BUTTON_ID_SUFFIX)}
                        type="button"
                        className={cn(
                          "absolute top-1/2 right-12 -translate-x-2 -translate-y-1/2 text-sm transition-opacity",
                          "flex gap-1 rounded border border-solid border-grey-400 bg-white px-2 py-1 text-grey-700 shadow",
                          showMoreButton
                            ? "opacity-0 group-hover:opacity-100"
                            : "pointer-events-none opacity-0",
                          moreMenuActive && showMoreButton ? "opacity-100" : "",
                        )}
                        onClick={(e) => e.stopPropagation()}
                      >
                        More <IconDots size={16} />
                      </button>
                    </DropdownMenuTrigger>

                    <DropdownMenuContent onClick={(event) => event.stopPropagation()}>
                      {kebabMenuContent}
                    </DropdownMenuContent>
                  </DropdownMenu>

                  <DropdownMenu
                    onOpenChange={(open) => {
                      setActiveMoreMenuRowId(open ? row.id : undefined)
                    }}
                  >
                    <DropdownMenuTrigger asChild>
                      <Button
                        id={table.getElementId(KEBAB_MENU_BUTTON_ID_SUFFIX)}
                        iconOnly
                        variant="neutral"
                        noBorder
                        className="my-1"
                        icon={IconDotsVertical}
                        type="button"
                        onClick={(e) => e.stopPropagation()}
                      />
                    </DropdownMenuTrigger>

                    <DropdownMenuContent onClick={(event) => event.stopPropagation()}>
                      {kebabMenuContent}
                    </DropdownMenuContent>
                  </DropdownMenu>
                </>
              ) : null}
            </td>
          )
        }

        const colParent = cell.column.parent
        const lastParentChild =
          colParent?.getLeafColumns().at(-1)?.id === cell.column.id

        return (
          <td
            className={cn(
              "h-full px-4 py-3 align-bottom text-sm text-black",
              "border-b border-solid border-colors-border-secondary last-of-type:border-r-0",
              cell.column.columnDef.meta?.cellClassName,
              {
                "sticky z-10 bg-white transition-colors group-hover:bg-colors-bg-primary-screen group-aria-selected:bg-brand-primary-100":
                  cell.column.getIsPinned() === "left",
              },
              typeof classNameCell === "function" ? classNameCell(cell) : classNameCell,
              lastParentChild && "border-r",
            )}
            key={cell.id}
            style={{
              width: cell.column.columnDef.size ? cell.column.getSize() : undefined,
              ...(cell.column.columnDef.meta?.fixedSize
                ? {
                    minWidth: cell.column.getSize(),
                    maxWidth: cell.column.getSize(),
                  }
                : {}),
              ...(cell.column.getIsPinned() == "left"
                ? {
                    left: `${cell.column.getStart("left")}px`,
                  }
                : {}),
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </td>
        )
      })}
    </tr>
  )
}

const Memo_TableBodyRow = TableBodyRow as typeof TableBodyRow

DataTable.ActiveFiltersList = ActiveFiltersList
DataTable.FilterButton = FilterButton
DataTable.ClearFiltersButton = ClearFiltersButton
DataTable.GlobalFilter = GlobalFilter
